import { useContext, useMemo, useState } from 'react';
import {useQuery} from 'react-query';
import {Box, Cluster, Flex, Stack} from '../../shared/layouts';
import Card from '../../shared/Card';
import Header from '../../shared/Header';
import axios from 'axios';
import Table from '../../shared/Table';
import DotsIcon from 'imagesUwKluis/svg/dots.svg';
import WithPopup from '../../shared/WithPopup';
import {useNavigate} from 'react-router-dom';
import Modal from '../../shared/Modal';
import Button from '../../shared/Button';
import {settingsDateFormatter} from '../../../utils/dateHelpers';
import {useTranslation} from "react-i18next";
import useIconLibrary from "../../../hooks/useIconLibrary";
import MessagebarContext from "../../../context/MessagebarContext";

const columns = [
  {
    accessor: 'name',
    width: 300 * 0.33,
  },
  {
    accessor: 'createdAt',
    width: 300 * 0.33,
  },
  {
    accessor: 'actions',
    width: 300 * 0.1,
    align: 'right',
  },
];

const ExtraUsersSection = () => {
  const { t } = useTranslation();
  const [activeDots, setActiveDots] = useState(null);
  const [modal, setModal] = useState({});
  const navigate = useNavigate();
  const { AddPersonIcon } = useIconLibrary();
  const { setAlertMessage } = useContext(MessagebarContext);

  const { data: consumers = {}, refetch } = useQuery(
    ["connectedUsers"],
    async () => {
      const { data } = await axios.get(`/user/settings/users`);
      return data.connected_consumers;
    }
  );

  const data = useMemo(
    () =>
      Object.values(consumers).map((consumer) => ({
        name: `${consumer.first_name} ${consumer.last_name}`,
        createdAt: t('Toegevoegd op {{date}}', {
          date: settingsDateFormatter(new Date(consumer.created_at)),
        }),
        actions: !consumer.is_main_consumer && (
          <>
            <WithPopup
              content={
                <Box>
                  <Stack space="m">
                    <span
                      onClick={() => {
                        setActiveDots(null);
                        setModal({
                          id: consumer.id,
                          isOpen: true,
                        });
                      }}
                      css={({ colors }) => ({
                        color: colors.alert,
                        cursor: 'pointer',
                      })}
                    >
                      {t('Verwijderen')}
                    </span>
                  </Stack>
                </Box>
              }
              isOpen={activeDots === consumer.id}
            >
              <DotsIcon
                onClick={() => {
                  setActiveDots((prevId) =>
                    prevId === consumer.id ? null : consumer.id
                  );
                }}
              />
            </WithPopup>
          </>
        ),
      })),
    [consumers, activeDots]
  );

  return (
    <>
      <Modal
        isOpen={modal.isOpen}
        showCloseButton={false}
        onRequestClose={() => setModal({ isOpen: false })}
        showProgressBar
        progressBarColor="alert"
      >
        <Box fontScale="xl" pb="l">{t(`Gebruiker verwijderen`)}</Box>
        <Stack space="3xl">
          <Box>{t('Weet je zeker dat je deze gebruiker wilt verwijderen?')}</Box>
          <Flex justifyContent="flex-end">
            <Cluster space="m">
              <Button
                onClick={() => setModal({ isOpen: false })}
                variant="alert-outline"
              >
                {t('Annuleren')}
              </Button>
              <Button
                onClick={() => {
                  axios
                    .delete(`/user/settings/users/${modal.id}`)
                    .then(() => {
                      setActiveDots(null);
                      setModal({ isOpen: false });
                      refetch();
                    })
                    .catch((err) =>
                      err?.response?.status === 422 && err?.response?.data?.message
                        ? setAlertMessage(err.response.data.message)
                        : setAlertMessage(t('Er is iets mis gegaan'))
                    );
                }}
                variant="alert"
              >
                {t('Verwijderen')}
              </Button>
            </Cluster>
          </Flex>
        </Stack>
      </Modal>
      <Card>
        <Stack space="m" width="100%">
          <Header
            title={t('Extra gebruikers')}
            icon={<AddPersonIcon height="2rem" width="2rem" />}
            variant="section"
          />
          <Box>{t('Voeg hier eventuele extra gebruikers toe.')}</Box>
          {Object.keys(consumers).length > 0 && (
            <Table columns={columns} data={data} />
          )}
          <Button
            variant={'primary-outline'}
            onClick={() => navigate(`/user/settings/add-extra-user`)}
            width={'100%'}
          >
            {t('Voeg extra gebruiker toe')}
          </Button>
        </Stack>
      </Card>
    </>
  );
};

export default ExtraUsersSection;
